/**
 * AuthModal - 高级登录弹窗
 * 轻量毛玻璃 + 柔和阴影 + 圆润设计
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useMessage } from '../../../context/MessageContext';
import logoIcon from '../../../assets/images/link_logo.svg';

// 步骤组件
import { SelectMethodStep } from './steps';

// 步骤枚举
const STEPS = {
    SELECT_METHOD: 'select_method',
};

const AuthModal = ({ isOpen, onClose }) => {
    const [currentStep, setCurrentStep] = useState(STEPS.SELECT_METHOD);

    const { loginWithToken } = useAuth();
    const { showMessage } = useMessage();

    // 重置状态
    const resetState = useCallback(() => {
        setCurrentStep(STEPS.SELECT_METHOD);
    }, []);

    // 关闭弹窗时重置
    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen, resetState]);

    // 登录成功处理
    const handleLoginSuccess = useCallback((response) => {
        const {
            token,
            access,
            refresh,
            user,
            is_new_user,
            message,
        } = response || {};
        const accessToken = token || access;
        loginWithToken(accessToken, refresh, user);
        showMessage('success', message || (is_new_user ? '注册成功！' : '登录成功！'));
        onClose?.();
    }, [loginWithToken, showMessage, onClose]);

    // 登录失败处理 — 表单内已有 inline 错误提示，不再重复 toast
    const handleLoginError = useCallback(() => {
        // 子组件 PasswordLoginStep 等已自带 inline error，此处无需 toast
    }, []);

    const renderStep = () => {
        switch (currentStep) {
            case STEPS.SELECT_METHOD:
                return (
                    <SelectMethodStep
                        onWechatSuccess={handleLoginSuccess}
                        onLoginSuccess={handleLoginSuccess}
                        onLoginError={handleLoginError}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* 深色遮罩层 */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-gray-900/30 backdrop-blur-sm z-50"
                    />

                    {/* 弹窗主体 */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                        className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
                    >
                        {/* 卡片容器 - 毛玻璃质感 */}
                        <div className="relative w-full max-w-[420px] bg-white rounded-3xl shadow-2xl border border-white/20 overflow-hidden pointer-events-auto">

                            {/* 背景光斑装饰 */}
                            <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-100 rounded-full blur-3xl opacity-60" />
                            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-100 rounded-full blur-3xl opacity-60" />

                            {/* 头部区域 */}
                            <div className="relative px-6 pt-6 pb-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {/* Logo - 渐变色块容器 */}
                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                                            <img src={logoIcon} alt="AI门道" className="w-6 h-6 brightness-0 invert" />
                                        </div>
                                        <div>
                                            {/* 品牌字渐变 */}
                                            <h2 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                                登录 AI门道
                                            </h2>
                                            <p className="text-xs text-blue-500 font-medium">让创作更简单</p>
                                        </div>
                                    </div>

                                    {/* 关闭按钮 - 隐蔽风格 */}
                                    <button
                                        onClick={onClose}
                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* 内容区 */}
                            <div className="relative px-6 pb-6">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentStep}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2, ease: 'easeOut' }}
                                    >
                                        {renderStep()}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default AuthModal;
